import type { ActorKind, AuditActor, AuditEntry } from '@/api/generated';

/**
 * Who did something, and what it was done to, in words.
 *
 * Kept apart from the table and the drawer so the two say it the same way,
 * and so the account page's panel can say it too.
 */

/** The kind of person, as the drawer and the table's hover text say it. */
export const ACTOR_KIND_LABELS: Record<ActorKind, string> = {
  ACTIVIST_STAFF: 'Activist staff',
  ORGANIZATION_USER: 'Organization user',
  SYSTEM: 'System',
};

/**
 * The person's name, or "System" for a write made on nobody's behalf.
 *
 * A person whose name the log did not keep falls back to their address, then
 * to the kind, so the cell is never blank.
 */
export function actorLabel(actor: AuditActor): string {
  if (actor.kind === 'SYSTEM') return 'System';
  return actor.name || actor.email || ACTOR_KIND_LABELS[actor.kind] || 'Unknown';
}

/** The words for a target's type. */
const TARGET_TYPE_LABELS: Record<string, string> = {
  organization: 'Organization',
  portal_user: 'User',
};

export function targetTypeLabel(type: string | null | undefined): string {
  if (!type) return '';
  return TARGET_TYPE_LABELS[type] ?? type.replace(/_/g, ' ');
}

/** What the entry was about, as one short line. */
export function targetLabel(entry: AuditEntry): string {
  return entry.target.label || targetTypeLabel(entry.target.type) || '—';
}

/** The organization an entry belongs to; "Platform" for one that belongs to none. */
export function organizationLabel(entry: AuditEntry): string {
  return entry.organization?.name ?? 'Platform';
}
